import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Card,
  CardHeader,
  Divider,
  Grid,
  LinearProgress,
  Skeleton,
  Stack,
  Typography,
} from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import Chart from "../charts/chart";
import useChart from "../charts/use-chart";
import { formatearPorcentaje, limitarPorcentajeGrafico } from "./kpi-utils";

const ETIQUETAS_TIPO = {
  PLAN: "Planes",
  ESPECIAL: "Especiales",
  INTERNA: "Internas",
  OTRO: "Otros",
};

AppKpiCumplimientoTareas.propTypes = {
  data: PropTypes.shape({
    actual: PropTypes.object,
    anterior: PropTypes.object,
    periodoActual: PropTypes.object,
    periodoAnterior: PropTypes.object,
    porTipo: PropTypes.array,
    variacionPuntosPorcentuales: PropTypes.number,
  }),
  error: PropTypes.bool,
  loading: PropTypes.bool,
};

export default function AppKpiCumplimientoTareas({
  data,
  error = false,
  loading = false,
}) {
  const theme = useTheme();
  const actual = data?.actual || {};
  const cumplimiento = actual.cumplimientoPorcentaje;
  const variacion = data?.variacionPuntosPorcentuales;
  const porTipo = data?.porTipo || [];
  const periodoActual = data?.periodoActual
    ? `${data.periodoActual.inicio} a ${data.periodoActual.fin}`
    : "";
  const periodoAnterior = data?.periodoAnterior
    ? `${data.periodoAnterior.inicio} a ${data.periodoAnterior.fin}`
    : "";

  const chartOptions = useChart({
    chart: { sparkline: { enabled: true } },
    colors: [theme.palette.success.main],
    labels: ["Cumplimiento"],
    legend: { show: false },
    plotOptions: {
      radialBar: {
        hollow: { size: "66%" },
        track: { margin: 0 },
        dataLabels: {
          name: { offsetY: 24 },
          value: {
            offsetY: -16,
            fontSize: "28px",
            fontWeight: 700,
            formatter: () => formatearPorcentaje(cumplimiento),
          },
        },
      },
    },
    tooltip: { enabled: false },
  });

  if (loading) {
    return (
      <Card sx={{ minHeight: 360 }}>
        <CardHeader
          title={<Skeleton width={230} />}
          subheader={<Skeleton width={300} />}
        />
        <Grid container spacing={3} sx={{ p: 3 }}>
          <Grid item xs={12} md={5}>
            <Skeleton variant="circular" width={210} height={210} />
          </Grid>
          <Grid item xs={12} md={7}>
            <Stack spacing={2}>
              {[1, 2, 3].map((item) => (
                <Skeleton key={item} height={48} />
              ))}
            </Stack>
          </Grid>
        </Grid>
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={{ minHeight: 360 }}>
        <CardHeader
          title="Tasa de cumplimiento de tareas"
          subheader="Tareas completadas / tareas del período"
        />
        <Box sx={{ px: 3, pb: 3 }}>
          <Alert severity="error">
            No fue posible obtener el indicador. Intenta nuevamente.
          </Alert>
        </Box>
      </Card>
    );
  }

  if (!data) {
    return (
      <Card sx={{ minHeight: 360 }}>
        <CardHeader
          title="Tasa de cumplimiento de tareas"
          subheader="Tareas completadas / tareas del período"
        />
        <Box sx={{ px: 3, pb: 3 }}>
          <Alert severity="info">
            No hay información disponible para los filtros seleccionados.
          </Alert>
        </Box>
      </Card>
    );
  }

  const colorVariacion =
    variacion > 0
      ? "success.main"
      : variacion < 0
        ? "error.main"
        : "text.secondary";

  return (
    <Card
      sx={{
        minHeight: 360,
        borderTop: `4px solid ${theme.palette.success.main}`,
      }}
    >
      <CardHeader
        title="Tasa de cumplimiento de tareas"
        subheader={`Tareas completadas / tareas del período · ${periodoActual}`}
        sx={{ pb: 1 }}
      />

      <Grid container spacing={4} alignItems="center" sx={{ px: 3, pb: 3 }}>
        <Grid item xs={12} md={4}>
          <Stack alignItems="center">
            <Chart
              type="radialBar"
              series={[limitarPorcentajeGrafico(cumplimiento)]}
              options={chartOptions}
              width={220}
              height={220}
            />
            <Typography
              variant="body2"
              sx={{ color: colorVariacion, textAlign: "center" }}
            >
              {variacion === null || variacion === undefined
                ? "Sin base comparable en el período anterior"
                : `${variacion > 0 ? "+" : ""}${variacion.toLocaleString(
                    "es-CL",
                  )} pp respecto de ${periodoAnterior}`}
            </Typography>
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Stack
            divider={<Divider flexItem sx={{ borderStyle: "dashed" }} />}
            spacing={2.5}
          >
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Tareas del período</Typography>
              <Typography variant="subtitle2">{actual.total || 0}</Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">Completadas</Typography>
              <Typography variant="subtitle2">
                {actual.completadas || 0}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="body2">No completadas</Typography>
              <Typography variant="subtitle2">
                {actual.noCompletadas || 0}
              </Typography>
            </Stack>
          </Stack>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
            Desglose por tipo
          </Typography>
          <Stack spacing={1.5}>
            {porTipo.map((item) => (
              <Box key={item.tipo}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2">
                    {ETIQUETAS_TIPO[item.tipo] || item.tipo}
                  </Typography>
                  <Typography variant="subtitle2">
                    {formatearPorcentaje(item.actual.cumplimientoPorcentaje)}
                  </Typography>
                </Stack>
                <LinearProgress
                  variant="determinate"
                  value={limitarPorcentajeGrafico(
                    item.actual.cumplimientoPorcentaje,
                  )}
                  color="success"
                  sx={{ mt: 0.75, height: 6, borderRadius: 1 }}
                />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {item.actual.completadas} de {item.actual.total}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Card>
  );
}
